import { Capacitor } from '@capacitor/core';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Safe runtime check - returns false in any web/browser context.
export const isNativePlatform = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

// True only inside a Capacitor-wrapped iOS build (TestFlight / App Store / device).
// Returns false on web, Android, and any non-Capacitor context.
export const isNativeIOS = () => {
  try {
    return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
  } catch {
    return false;
  }
};

// Apple's native sheet should respond in a few seconds. If the plugin call
// never resolves (rare native bridge stalls), this guarantees the caller's
// UI unsticks instead of spinning forever. Not an Apple-imposed timeout —
// purely a client-side safety net (Apple review Issue 5).
const NATIVE_APPLE_TIMEOUT_MS = 15000;

export async function getNativeAppleCredential() {
  console.log('[AppleAuth] native-authorize-start');
  const { SignInWithApple } = await import('@capacitor-community/apple-sign-in');

  const authorizePromise = SignInWithApple.authorize({
    // iOS uses the app bundle id and ignores these; required so the call
    // doesn't throw synchronously in non-iOS/browser contexts.
    clientId: process.env.REACT_APP_APPLE_CLIENT_ID || 'com.zenscroll.app',
    redirectURI: `${window.location.origin}/auth/apple`,
    scopes: 'email name',
  });
  const timeoutPromise = new Promise((_, reject) => {
    setTimeout(() => {
      const err = new Error('Apple Sign-In did not respond in time.');
      err.code = 'NATIVE_TIMEOUT';
      reject(err);
    }, NATIVE_APPLE_TIMEOUT_MS);
  });
  const result = await Promise.race([authorizePromise, timeoutPromise]);

  const resp = (result && result.response) || {};
  const identityToken = resp.identityToken;
  console.log('[AppleAuth] native-authorize-response', { hasIdentityToken: !!identityToken, hasNonce: !!resp.nonce });
  if (!identityToken) {
    throw new Error('No identity token returned from Apple');
  }

  const fullName =
    resp.givenName || resp.familyName
      ? { givenName: resp.givenName || null, familyName: resp.familyName || null }
      : null;

  return {
    identityToken,
    nonce: resp.nonce || null,
    fullName,
  };
}

/**
 * Native iOS Sign in with Apple via @capacitor-community/apple-sign-in.
 * `consent` is optional and is forwarded only during signup-mode consent capture.
 */
export async function signInWithAppleNative(consent = null) {
  const credential = await getNativeAppleCredential();
  const payload = {
    identity_token: credential.identityToken,
    nonce: credential.nonce,
    full_name: credential.fullName,
  };

  if (consent && typeof consent === 'object') {
    Object.assign(payload, consent);
  }

  console.log('[AppleAuth] backend-auth-start');
  const backendRes = await axios.post(`${API}/auth/apple/native`, payload);
  console.log('[AppleAuth] backend-auth-success', {
    hasToken: !!backendRes.data?.token,
    isNewUser: !!backendRes.data?.is_new_user,
  });
  return backendRes.data;
}

export async function getAppleReauthToken() {
  const credential = await getNativeAppleCredential();
  return {
    identity_token: credential.identityToken,
    nonce: credential.nonce,
  };
}
